import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import { visuallyHidden } from "@mui/utils"
import { ChangeEvent, MouseEvent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { RootState } from "../../store"

interface Data {
	bankId: string
	corporateSenderId: number
	totalFiles: number
	drafts: number
	errors: number
	plicksCount: number
	totalAmount: number
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1
	}
	if (b[orderBy] > a[orderBy]) {
		return 1
	}
	return 0
}

type Order = "asc" | "desc"

function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string }
) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy)
}

interface HeadCell {
	disablePadding: boolean
	id: keyof Data
	label: string
	numeric: boolean
}

const headCells: readonly HeadCell[] = [
	{
		id: "bankId",
		numeric: false,
		disablePadding: false,
		label: "Banca",
	},
	{
		id: "corporateSenderId",
		numeric: false,
		disablePadding: false,
		label: "Corporate",
	},
	{
		id: "totalFiles",
		numeric: true,
		disablePadding: false,
		label: "Totale distinte",
	},
	{
		id: "drafts",
		numeric: true,
		disablePadding: false,
		label: "File in draft",
	},
	{
		id: "errors",
		numeric: true,
		disablePadding: false,
		label: "File in errore",
	},
	{
		id: "plicksCount",
		numeric: true,
		disablePadding: false,
		label: "Plick creati",
	},
	{
		id: "totalAmount",
		numeric: true,
		disablePadding: false,
		label: "Totale Plick",
	},
]

interface EnhancedTableProps {
	onRequestSort: (event: MouseEvent<unknown>, property: keyof Data) => void
	order: Order
	orderBy: string
}

function EnhancedTableHead(props: EnhancedTableProps) {
	const { order, orderBy, onRequestSort } = props
	const { t } = useTranslation()

	const headCells: readonly HeadCell[] = [
		{
			id: "bankId",
			numeric: false,
			disablePadding: false,
			label: t("table.file.bank", { ns: "dashboard" }),
		},
		{
			id: "corporateSenderId",
			numeric: false,
			disablePadding: false,
			label: t("table.file.corporate", { ns: "dashboard" }),
		},
		{
			id: "totalFiles",
			numeric: true,
			disablePadding: false,
			label: t("table.file.corporate", { ns: "dashboard" }),
		},
		{
			id: "drafts",
			numeric: true,
			disablePadding: false,
			label: t("table.file.draft", { ns: "dashboard" }),
		},
		{
			id: "errors",
			numeric: true,
			disablePadding: false,
			label: t("table.file.error", { ns: "dashboard" }),
		},
		{
			id: "plicksCount",
			numeric: true,
			disablePadding: false,
			label: t("table.file.plickCreated", { ns: "dashboard" }),
		},
		{
			id: "totalAmount",
			numeric: true,
			disablePadding: false,
			label: t("table.file.plickTotal", { ns: "dashboard" }),
		},
	]

	const createSortHandler =
		(property: keyof Data) => (event: MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.numeric ? "right" : "left"}
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={orderBy === headCell.id ? order : false}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	)
}

export default function StatisticFileTable() {
	const dataSource = useSelector(
		(state: RootState) => state.statistics?.file?.data
	)
	const [order, setOrder] = useState<Order>("asc")
	const [orderBy, setOrderBy] = useState<keyof Data>("bankId")
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(5)

	const handleRequestSort = (
		event: MouseEvent<unknown>,
		property: keyof Data
	) => {
		const isAsc = orderBy === property && order === "asc"
		setOrder(isAsc ? "desc" : "asc")
		setOrderBy(property)
	}

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage)
	}

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10))
		setPage(0)
	}

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		dataSource && page > 0
			? Math.max(0, (1 + page) * rowsPerPage - dataSource.length)
			: 0

	const visibleRows = useMemo(
		() =>
			[...(dataSource ?? [])]
				.sort(getComparator(order, orderBy))
				.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
		[order, orderBy, page, rowsPerPage]
	)

	return (
		<Box sx={{ width: "100%" }}>
			<Paper sx={{ width: "100%", mb: 2 }}>
				<TableContainer>
					<Table
						sx={{ minWidth: 750 }}
						aria-labelledby="tableTitle"
						size={"medium"}
					>
						<EnhancedTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
						/>
						<TableBody>
							{visibleRows.map((row, index) => {
								const labelId = `enhanced-table-checkbox-${index}`

								return (
									<TableRow
										hover
										tabIndex={-1}
										key={`tableRow_${index}`}
										sx={{ cursor: "pointer" }}
									>
										<TableCell component="th" id={labelId} scope="row">
											{row.bankId}
										</TableCell>
										<TableCell align="left">{row.corporateSenderId}</TableCell>
										<TableCell align="right">{row.totalFiles}</TableCell>
										<TableCell align="right">{row.drafts}</TableCell>
										<TableCell align="right">{row.errors}</TableCell>
										<TableCell align="right">{row.plicksCount}</TableCell>
										<TableCell align="right">{row.totalAmount}</TableCell>
									</TableRow>
								)
							})}
							{emptyRows > 0 && (
								<TableRow
									style={{
										height: 53 * emptyRows,
									}}
								>
									<TableCell colSpan={6} />
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={dataSource ? dataSource.length : 0}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</Box>
	)
}
